import React, { useRef } from 'react';
import { Camera, User } from 'lucide-react';
import { cn } from '@/lib/utils';

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * Premium circular profile photo picker (stores data URL via parent onChange).
 */
export default function ProfilePhotoUpload({
  value,
  onChange,
  name = '',
  label = 'Profile photo',
  hint = 'JPG or PNG, max 2 MB',
  size = 'lg',
  className,
}) {
  const inputRef = useRef(null);
  const sizes = {
    md: { box: 'h-20 w-20', icon: 'h-8 w-8', badge: 'h-7 w-7' },
    lg: { box: 'h-24 w-24', icon: 'h-10 w-10', badge: 'h-8 w-8' },
  };
  const s = sizes[size] || sizes.lg;
  const initial = (name || 'U').trim().charAt(0).toUpperCase();

  const pickFile = (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      onChange?.(value, { error: 'Photo must be under 2 MB' });
      return;
    }
    if (!ACCEPT.split(',').some((m) => file.type === m) && !/\.(jpe?g|png|webp)$/i.test(file.name)) {
      onChange?.(value, { error: 'Use JPG, PNG or WebP' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange?.(reader.result, { error: null, file });
    reader.readAsDataURL(file);
  };

  return (
    <div className={cn('flex flex-col items-center gap-3 sm:flex-row sm:items-start', className)}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={cn(
          'group relative shrink-0 overflow-hidden rounded-full border-2 border-[#C9A84C]/45',
          'bg-gradient-to-br from-[#a07830]/30 to-[#0a1628] shadow-[0_0_24px_rgba(201,168,76,0.2)]',
          'transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/50',
          s.box,
        )}
        aria-label={label}
      >
        {value ? (
          <img src={value} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-2xl font-bold text-[#E8D5A3]">
            {initial}
          </span>
        )}
        <span
          className={cn(
            'absolute bottom-0 right-0 flex items-center justify-center rounded-full',
            'border border-[#0a1628] bg-[#C9A84C] text-[#0a1628] shadow-md',
            s.badge,
          )}
        >
          <Camera className="h-3.5 w-3.5" strokeWidth={2.25} />
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          pickFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <div className="min-w-0 text-center sm:text-left">
        <p className="flex items-center justify-center gap-1.5 text-sm font-bold text-white sm:justify-start">
          <User className="h-4 w-4 text-[#C9A84C]" />
          {label}
        </p>
        <p className="mt-1 text-xs text-white/50">{hint}</p>
        <button
          type="button"
          className="mt-2 text-xs font-semibold text-[#C9A84C] hover:text-[#e2c97e]"
          onClick={() => inputRef.current?.click()}
        >
          {value ? 'Change photo' : 'Upload photo'}
        </button>
        {value && (
          <button
            type="button"
            className="ml-3 text-xs font-semibold text-white/40 hover:text-white/70"
            onClick={() => onChange?.('', { error: null })}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
